import { useEffect, useRef } from "react";

const AGENTS = [
  { label: "SALES", angle: 0.0, speed: 0.00011, r: 0.30, c: "#00F0FF" },
  { label: "MARKETING", angle: 0.8, speed: 0.00013, r: 0.34, c: "#7000FF" },
  { label: "OPERATIONS", angle: 1.6, speed: 0.0001, r: 0.29, c: "#FF0055" },
  { label: "FINANCE", angle: 2.4, speed: 0.00014, r: 0.35, c: "#00F0FF" },
  { label: "HR", angle: 3.2, speed: 0.00012, r: 0.31, c: "#7000FF" },
  { label: "EXECUTIVE", angle: 4.0, speed: 0.0001, r: 0.36, c: "#FF0055" },
  { label: "GESTIONALI", angle: 4.8, speed: 0.00013, r: 0.3, c: "#00F0FF" },
  { label: "IMPIANTI", angle: 5.6, speed: 0.00011, r: 0.35, c: "#7000FF" },
];

const NeuralCanvas = ({ className = "" }) => {
  const canvasRef = useRef(null);
  const mouse = useRef({ x: 0.5, y: 0.5 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let w = 0;
    let h = 0;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const onMouse = (e) => {
      mouse.current = { x: e.clientX / window.innerWidth, y: e.clientY / window.innerHeight };
    };
    window.addEventListener("mousemove", onMouse);

    const particles = Array.from({ length: 42 }, () => ({
      x: Math.random(),
      y: Math.random(),
      vx: (Math.random() - 0.5) * 0.00025,
      vy: (Math.random() - 0.5) * 0.00025,
      r: Math.random() * 1.6 + 0.5,
    }));

    const nodes = AGENTS.map((a) => ({ ...a }));

    const draw = (t) => {
      ctx.clearRect(0, 0, w, h);
      const small = w < 768;
      const cx = w * (small ? 0.5 : 0.7) + (mouse.current.x - 0.5) * 40;
      const cy = h * (small ? 0.4 : 0.48) + (mouse.current.y - 0.5) * 40;
      const base = Math.min(w, h);

      // background particles
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > 1) p.vx *= -1;
        if (p.y < 0 || p.y > 1) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x * w, p.y * h, p.r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(255,255,255,0.28)";
        ctx.fill();
      });

      // links + moving pulses
      nodes.forEach((n, i) => {
        n.angle += n.speed * 16;
        const breath = 1 + 0.07 * Math.sin(t * 0.0012 + i);
        const nx = cx + Math.cos(n.angle) * base * n.r * breath;
        const ny = cy + Math.sin(n.angle) * base * n.r * breath * 0.82;
        n.px = nx;
        n.py = ny;

        const grad = ctx.createLinearGradient(cx, cy, nx, ny);
        grad.addColorStop(0, "rgba(112,0,255,0.35)");
        grad.addColorStop(1, n.c + "55");
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(nx, ny);
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1;
        ctx.stroke();

        const p = (t * 0.00035 + i * 0.13) % 1;
        const pxx = cx + (nx - cx) * p;
        const pyy = cy + (ny - cy) * p;
        ctx.beginPath();
        ctx.arc(pxx, pyy, 2.2, 0, Math.PI * 2);
        ctx.shadowColor = n.c;
        ctx.shadowBlur = 10;
        ctx.fillStyle = n.c;
        ctx.fill();
        ctx.shadowBlur = 0;
      });

      // agent nodes
      nodes.forEach((n) => {
        ctx.beginPath();
        ctx.arc(n.px, n.py, 5, 0, Math.PI * 2);
        ctx.shadowColor = n.c;
        ctx.shadowBlur = 16;
        ctx.fillStyle = n.c;
        ctx.fill();
        ctx.shadowBlur = 0;

        ctx.font = "600 10px Outfit, sans-serif";
        ctx.textAlign = "center";
        ctx.fillStyle = "rgba(255,255,255,0.75)";
        ctx.fillText(n.label, n.px, n.py - 12);
      });

      // core brain
      const pulse = 1 + 0.12 * Math.sin(t * 0.002);
      const coreR = (small ? 34 : 46) * pulse;
      const rg = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreR * 2.4);
      rg.addColorStop(0, "rgba(0,240,255,0.9)");
      rg.addColorStop(0.35, "rgba(112,0,255,0.55)");
      rg.addColorStop(1, "rgba(255,0,85,0)");
      ctx.beginPath();
      ctx.arc(cx, cy, coreR * 2.4, 0, Math.PI * 2);
      ctx.fillStyle = rg;
      ctx.fill();

      ctx.beginPath();
      ctx.arc(cx, cy, coreR, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(8,8,14,0.85)";
      ctx.fill();
      ctx.strokeStyle = "rgba(0,240,255,0.9)";
      ctx.lineWidth = 1.5;
      ctx.stroke();

      ctx.font = "700 11px Unbounded, sans-serif";
      ctx.textAlign = "center";
      ctx.fillStyle = "#ffffff";
      ctx.fillText("X", cx, cy + 4);

      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouse);
    };
  }, []);

  return <canvas ref={canvasRef} className={className} data-testid="neural-canvas" />;
};

export default NeuralCanvas;
